import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { LUMINA_APP_VERSION } from '../config/version';

interface UpdateContextType {
  isUpdateAvailable: boolean;
  setBusyState: (taskId: string, isBusy: boolean) => void;
  isAppBusy: boolean;
}

const UpdateContext = createContext<UpdateContextType | undefined>(undefined);

export function UpdateProvider({ children }: { children: React.ReactNode }) {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [busyStates, setBusyStates] = useState<Record<string, boolean>>({});

  const isAppBusy = Object.values(busyStates).some(state => state);

  const checkForUpdate = useCallback(async () => {
    try {
      const docRef = doc(db, 'system', 'app_config');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const latestVersion = snap.data().latestVersion;
        if (typeof latestVersion === 'number' && latestVersion > LUMINA_APP_VERSION) {
          setIsUpdateAvailable(true);
        }
      }
    } catch (e: any) {
      if (e?.code === 'unavailable' || e?.message?.includes('offline')) {
        // Silently ignore offline errors
        return;
      }
      console.error("Failed to check for updates:", e);
    }
  }, []);

  useEffect(() => {
    checkForUpdate();

    const interval = setInterval(() => {
      checkForUpdate();
    }, 5 * 60 * 1000); // 5 minutes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isAppBusy) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAppBusy]);

  const setBusyState = useCallback((taskId: string, isBusy: boolean) => {
    setBusyStates(prev => {
      if (prev[taskId] === isBusy) return prev;
      return { ...prev, [taskId]: isBusy };
    });
  }, []);

  return (
    <UpdateContext.Provider value={{ isUpdateAvailable, setBusyState, isAppBusy }}>
      {children}
      {isUpdateAvailable && <UpdateBanner isBusy={isAppBusy} />}
    </UpdateContext.Provider>
  );
}

function UpdateBanner({ isBusy }: { isBusy: boolean }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] bg-bg-alt border border-border-main text-text-main p-4 rounded-xl shadow-2xl flex flex-col gap-3 max-w-sm animate-in slide-in-from-bottom-4 slide-in-from-right-4 duration-300">
      <div className="font-bold flex items-center gap-2 text-indigo-500">
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
        New Update Available
      </div>
      <p className="text-sm opacity-90 text-text-alt">
        {isBusy 
          ? "A new version of Lumina is available. Please finish your current task before refreshing."
          : "A new version of Lumina is available. Refresh now to get the latest features."}
      </p>
      <div className="flex gap-2 justify-end mt-1">
        {isBusy ? (
          <button 
            onClick={() => setDismissed(true)}
            className="px-4 py-2 border border-border-main text-xs font-semibold hover:bg-bg-main rounded-lg transition-colors"
          >
            Refresh later
          </button>
        ) : (
          <>
            <button 
              onClick={() => setDismissed(true)}
              className="px-4 py-2 border border-border-main text-xs font-semibold hover:bg-bg-main rounded-lg transition-colors"
            >
              Later
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
            >
              Refresh now
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function useUpdate() {
  const context = useContext(UpdateContext);
  if (!context) {
    throw new Error('useUpdate must be used within an UpdateProvider');
  }
  return context;
}
